import React from "react";
import styled, { keyframes } from "styled-components";

/** Loader circle diameter in px. */
export const SPINNER_SIZE = {
  small: 24,
  large: 48,
} as const;

/** Reserved block height when Spinner is not inline (prevents layout jumps). */
export const SPINNER_SLOT_HEIGHT = {
  small: 34,
  large: 88,
} as const;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const prixClipFix = keyframes`
  0%, 100% {
    clip-path: inset(0% 0% 50% 0%);
  }
  50% {
    clip-path: inset(0% 50% 50% 0%);
  }
`;

const SpinnerContainer = styled.div<{ $small?: boolean; $inline?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;

  ${({ $inline, $small }): string => {
    const loaderSize = $small ? SPINNER_SIZE.small : SPINNER_SIZE.large;
    if ($inline) {
      return `
        width: ${loaderSize}px;
        height: ${loaderSize}px;
        min-height: ${loaderSize}px;
      `;
    }
    const slotHeight = $small
      ? SPINNER_SLOT_HEIGHT.small
      : SPINNER_SLOT_HEIGHT.large;
    return `
      width: 100%;
      height: ${slotHeight}px;
      min-height: ${slotHeight}px;
    `;
  }}
`;

const Loader = styled.div<{ $small?: boolean; light?: boolean }>`
  width: ${(props): string =>
    props.$small ? `${SPINNER_SIZE.small}px` : `${SPINNER_SIZE.large}px`};
  height: ${(props): string =>
    props.$small ? `${SPINNER_SIZE.small}px` : `${SPINNER_SIZE.large}px`};
  flex-shrink: 0;
  border-radius: 50%;
  position: relative;
  animation: ${rotate} 1s linear infinite;

  &::before,
  &::after {
    content: "";
    box-sizing: border-box;
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 5px solid
      ${(props): string =>
        props.light ? props.theme.colors.darkBg : props.theme.colors.primary};
    animation: ${prixClipFix} 2s linear infinite;
  }

  &::after {
    border-color: ${(props): string =>
      props.light ? props.theme.colors.darkGray : props.theme.colors.white};
    animation:
      ${prixClipFix} 2s linear infinite,
      ${rotate} 0.5s linear infinite reverse;
  }
`;

const Spinner = ({
  small,
  light,
  inline,
}: {
  small?: boolean;
  light?: boolean;
  /** Fixed loader-sized box; use inside buttons, toolbars, or reserved slots. */
  inline?: boolean;
}): React.JSX.Element => {
  return (
    <SpinnerContainer $small={small} $inline={inline}>
      <Loader light={light} $small={small} role="status" aria-label="Loading" />
    </SpinnerContainer>
  );
};

export default Spinner;
